import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { useRegister } from '@services/auth/mutations/useRegister'

import { AuthLayout } from '../components/AuthLayout'
import { AuthSuccess } from '../components/AuthSuccess'
import { ReferralCodeField } from '../components/ReferralCodeField'
import {
  getTermsUrl,
  isSupportedLanguage,
  toI18nextCode
} from '@/i18n/languages'

const PHONE_REGEX = /^(\+|00)?[1-9]\d{1,14}$/

const WEBSITE_URL =
  import.meta.env.VITE_WEBSITE_URL || 'https://najomnaagentura.sk'

/** External link inside a checkbox label — stop events so clicking it
 * follows the link instead of toggling the checkbox. Children are the
 * linked text chunk injected by <Trans>. */
function ConsentLink({
  href,
  children
}: {
  href: string
  children?: React.ReactNode
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      className="font-medium text-primary underline underline-offset-2 hover:opacity-80"
    >
      {children}
    </a>
  )
}

export function Register() {
  const { t, i18n } = useTranslation('auth')
  const [searchParams] = useSearchParams()

  // Terms live on the public website; the privacy section is an anchor there.
  const termsUrl = getTermsUrl(WEBSITE_URL, i18n.language)
  const gdprUrl = `${termsUrl}#section-10`
  const [referralCode, setReferralCode] = useState<string | null>(null)
  const [referrerName, setReferrerName] = useState<string | null>(null)

  // Handle language query parameter
  useEffect(() => {
    const langParam = searchParams.get('lang')
    if (isSupportedLanguage(langParam)) {
      i18n.changeLanguage(toI18nextCode(langParam))
      localStorage.setItem('preferred_language', langParam)
    }
  }, [searchParams, i18n])

  const { mutate: submitRegister, isPending, isSuccess } = useRegister()

  const schema = useMemo(
    () =>
      z.object({
        first_name: z.string().min(1, t('register.firstNameRequired')),
        last_name: z.string().min(1, t('register.lastNameRequired')),
        email: z.string().email(t('register.emailInvalid')),
        phone: z
          .string()
          .min(1, t('register.phoneRequired'))
          .regex(PHONE_REGEX, t('register.phoneInvalid')),
        terms: z.boolean().refine((v) => v, {
          message: t('register.termsRequired')
        }),
        dataProcessing: z.boolean().refine((v) => v, {
          message: t('register.dataProcessingRequired')
        })
      }),
    [t]
  )

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      terms: false,
      dataProcessing: false
    }
  })

  function onSubmit(values: z.infer<typeof schema>) {
    submitRegister({
      body: {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        phone: values.phone,
        ...(referralCode && { referral_code: referralCode })
      }
    })
  }

  if (isSuccess) {
    return (
      <AuthSuccess
        title={
          <>
            {t('register.successTitle1')}
            <br />
            {t('register.successTitle2')}
          </>
        }
        text={
          <>
            <p>{t('register.successText')}</p>
            {referrerName && (
              <p className="mt-4 rounded-xl bg-primary/10 px-4 py-3 text-sm text-primary">
                <strong>{t('register.referral.successMessage')}</strong>
                <br />
                {t('register.referral.successDescriptionSimple', {
                  referrer: referrerName
                })}
              </p>
            )}
          </>
        }
        footer={
          <>
            {t('register.haveAccount')}{' '}
            <Link
              to="/login"
              className="font-semibold text-primary hover:underline"
            >
              {t('register.login')}
            </Link>
          </>
        }
      />
    )
  }

  return (
    <AuthLayout
      title={t('register.title')}
      footer={
        <>
          {t('register.haveAccount')}{' '}
          <Link
            to="/login"
            className="font-semibold text-primary hover:underline"
          >
            {t('register.login')}
          </Link>
        </>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('register.firstName')}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="given-name"
                      placeholder={t('register.firstName')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('register.lastName')}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="family-name"
                      placeholder={t('register.lastName')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('register.email')}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder={t('register.email')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('register.phone')}</FormLabel>
                <FormControl>
                  <PhoneInput
                    value={field.value}
                    onChange={field.onChange}
                    invalid={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <ReferralCodeField
            onReferralCodeChange={setReferralCode}
            onReferralApplied={setReferrerName}
          />

          <div className="space-y-3 pt-1">
            <FormField
              control={form.control}
              name="terms"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <div className="flex items-start gap-3">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="mt-0.5"
                      />
                    </FormControl>
                    <FormLabel className="cursor-pointer text-sm font-normal leading-snug text-ink/80">
                      <Trans
                        t={t}
                        i18nKey="register.terms"
                        components={[<ConsentLink key="0" href={termsUrl} />]}
                      />
                    </FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="dataProcessing"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <div className="flex items-start gap-3">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="mt-0.5"
                      />
                    </FormControl>
                    <FormLabel className="cursor-pointer text-sm font-normal leading-snug text-ink/80">
                      <Trans
                        t={t}
                        i18nKey="register.dataProcessing"
                        components={[<ConsentLink key="0" href={gdprUrl} />]}
                      />
                    </FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={isPending}
          >
            {t('register.submit')}
          </Button>
        </form>
      </Form>
    </AuthLayout>
  )
}
